/**
 * structuredData.js — Schema.org JSON-LD structured data for all pages.
 *
 * All schemas follow Google's Rich Results guidelines.
 * Exported schemas are consumed by the <SEO> component on each page.
 *
 * Types in use:
 *   Organization, LocalBusiness, WebSite, Service, FAQPage,
 *   WebPage, AboutPage, ContactPage, BreadcrumbList, AggregateRating, Review
 */

/* ── Site constants ───────────────────────────────────────────── */
const BASE_URL = 'https://driven2win.com';
const LOGO_URL = `${BASE_URL}/driven2win-logo.png`;
const PHONE    = '+1-904-638-7200';
const EMAIL    = 'info@driven2win.com';

const ADDRESS = {
  '@type':         'PostalAddress',
  streetAddress:   '5285 Shad Road, Suite 1619',
  addressLocality: 'Jacksonville',
  addressRegion:   'FL',
  postalCode:      '32257',
  addressCountry:  'US',
};

const GEO = {
  '@type':    'GeoCoordinates',
  latitude:   30.2672,
  longitude: -81.6557,
};

const HOURS = {
  '@type':     'OpeningHoursSpecification',
  dayOfWeek:   ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  opens:       '09:00',
  closes:      '18:00',
};

const AGGREGATE_RATING = {
  '@type':       'AggregateRating',
  ratingValue:   '4.9',
  reviewCount:   '250',
  bestRating:    '5',
  worstRating:   '1',
};


/* ── Breadcrumb helper (used on all inner pages) ─────────────── */
export function breadcrumbSchema(items) {
  return {
    '@context':      'https://schema.org',
    '@type':         'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type':   'ListItem',
      position:  index + 1,
      name:      item.name,
      item:      item.url,
    })),
  };
}


/* ══════════════════════════════════════════════════════════════
   HOME PAGE
   ══════════════════════════════════════════════════════════════ */

/* Organization — used on: Home, About */
export const organizationSchema = {
  '@context':   'https://schema.org',
  '@type':      'Organization',
  '@id':        `${BASE_URL}/#organization`,
  name:         'Driven2WIN',
  legalName:    'Driven2WIN High-Powered Promotions',
  url:          BASE_URL,
  logo: {
    '@type':  'ImageObject',
    url:      LOGO_URL,
    width:    '1200',
    height:   '400',
  },
  image:        LOGO_URL,
  description:  'Nationwide automotive event marketing company — 23+ years producing high-impact offsite sales events, tent setups, direct mail, and social media campaigns for car, golf cart, motorcycle, ATV, and RV dealerships across all 50 states.',
  telephone:    PHONE,
  email:        EMAIL,
  address:      ADDRESS,
  foundingDate: '2003',
  numberOfEmployees: { '@type': 'QuantitativeValue', value: '25' },
  areaServed:   { '@type': 'Country', name: 'United States' },
  knowsAbout: [
    'Automotive Event Marketing',
    'Dealership Sales Events',
    'Offsite Sales Events',
    'Tent Setup and Permitting',
    'Direct Mail Marketing',
    'Facebook Event Marketing',
    'Golf Cart Dealer Events',
    'Motorcycle Dealer Events',
    'RV Dealer Events',
    'ATV Dealer Events',
  ],
  sameAs: [
    'https://www.facebook.com/driven2win',
    'https://www.linkedin.com/company/driven2win',
  ],
  contactPoint: {
    '@type':            'ContactPoint',
    telephone:          PHONE,
    contactType:        'sales',
    areaServed:         'US',
    availableLanguage:  'English',
    hoursAvailable:     'Mo-Fr 09:00-18:00',
  },
};

/* Local Business — used on: Home, Contact */
export const localBusinessSchema = {
  '@context':   'https://schema.org',
  '@type':      ['LocalBusiness', 'ProfessionalService'],
  '@id':        `${BASE_URL}/#localbusiness`,
  name:         'Driven2WIN',
  image:        LOGO_URL,
  url:          BASE_URL,
  telephone:    PHONE,
  email:        EMAIL,
  priceRange:   '$$$',
  address:      ADDRESS,
  geo:          GEO,
  openingHoursSpecification: HOURS,
  aggregateRating: AGGREGATE_RATING,
  serviceArea: {
    '@type': 'Country',
    name:    'United States',
  },
  hasMap: `https://www.google.com/maps/search/?api=1&query=5285+Shad+Road+Jacksonville+FL`,
};

/* WebSite — used on: Home only.
   Enables Google Sitelinks Search Box in search results. */
export const webSiteSchema = {
  '@context':     'https://schema.org',
  '@type':        'WebSite',
  '@id':          `${BASE_URL}/#website`,
  name:           'Driven2WIN',
  url:            BASE_URL,
  description:    'Nationwide automotive event marketing — offsite sales events, tent setups, direct mail & Facebook campaigns for car, golf cart, motorcycle, ATV, and RV dealers.',
  publisher:      { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  potentialAction: {
    '@type':       'SearchAction',
    target: {
      '@type':     'EntryPoint',
      urlTemplate: `${BASE_URL}/?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

/* Services overview — used on: Home, Services */
export const serviceSchema = {
  '@context':  'https://schema.org',
  '@type':     'Service',
  '@id':       `${BASE_URL}/services#service`,
  name:        'Automotive Dealership Event Marketing',
  serviceType: 'Automotive Event Marketing',
  description: 'Turnkey event marketing services for car, golf cart, motorcycle, ATV, and RV dealerships — offsite events, tent setups, direct mail, and social media campaigns with guaranteed results.',
  provider:    { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  areaServed:  { '@type': 'Country', name: 'United States' },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name:    'Dealership Event Marketing Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type':      'Service',
          name:         'Offsite Sales Events',
          url:          `${BASE_URL}/services/offsite-events`,
          description:  'High-traffic offsite sales events that bring qualified buyers to your dealership.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type':      'Service',
          name:         'Tent Setups & Permitting',
          url:          `${BASE_URL}/services/tent-setups`,
          description:  'Complete turnkey event infrastructure — tents, signage, permits, and full setup.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type':      'Service',
          name:         'Direct Mail Marketing',
          url:          `${BASE_URL}/services/direct-mail`,
          description:  'High-response direct mail campaigns targeting qualified buyers in your market.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type':      'Service',
          name:         'Facebook & Instagram Events',
          url:          `${BASE_URL}/services/facebook-events`,
          description:  'Viral social media campaigns generating 100K–400K impressions per event.',
        },
      },
    ],
  },
};


/* ══════════════════════════════════════════════════════════════
   SERVICE SUB-PAGES
   ══════════════════════════════════════════════════════════════ */

export const offsiteEventsSchema = {
  '@context':   'https://schema.org',
  '@type':      'Service',
  '@id':        `${BASE_URL}/services/offsite-events#service`,
  name:         'Offsite Sales Events for Dealerships',
  serviceType:  'Offsite Automotive Sales Event',
  description:  'Professional offsite sales events at high-traffic retail locations for car, golf cart, motorcycle, ATV, and RV dealers — generating massive qualified buyer traffic and guaranteed results.',
  provider:     { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  areaServed:   { '@type': 'Country', name: 'United States' },
  url:          `${BASE_URL}/services/offsite-events`,
  offers: {
    '@type':       'Offer',
    name:          'Offsite Sales Event Package',
    description:   'Turnkey offsite sales events with venue selection, permitting, marketing, and on-site support.',
    seller:        { '@type': 'Organization', name: 'Driven2WIN' },
  },
};

export const tentSetupsSchema = {
  '@context':   'https://schema.org',
  '@type':      'Service',
  '@id':        `${BASE_URL}/services/tent-setups#service`,
  name:         'Tent Setup & Event Permitting for Dealerships',
  serviceType:  'Event Infrastructure & Permitting',
  description:  'Turnkey tent setup and permitting — site selection, permit applications, tent installation, signage, lighting, and full event infrastructure nationwide.',
  provider:     { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  areaServed:   { '@type': 'Country', name: 'United States' },
  url:          `${BASE_URL}/services/tent-setups`,
};

export const directMailSchema = {
  '@context':   'https://schema.org',
  '@type':      'Service',
  '@id':        `${BASE_URL}/services/direct-mail#service`,
  name:         'Direct Mail Marketing for Dealerships',
  serviceType:  'Direct Mail Advertising',
  description:  'High-response direct mail campaigns for automotive, golf cart, motorcycle, and RV dealers — precision targeting, premium print, and response rates 3× the industry average.',
  provider:     { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  areaServed:   { '@type': 'Country', name: 'United States' },
  url:          `${BASE_URL}/services/direct-mail`,
};

export const facebookEventsSchema = {
  '@context':   'https://schema.org',
  '@type':      'Service',
  '@id':        `${BASE_URL}/services/facebook-events#service`,
  name:         'Facebook & Instagram Event Marketing for Dealerships',
  serviceType:  'Social Media Event Marketing',
  description:  'Viral Facebook and Instagram event campaigns for dealerships generating 100K–400K impressions, driving calls, showroom traffic, and online leads.',
  provider:     { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  areaServed:   { '@type': 'Country', name: 'United States' },
  url:          `${BASE_URL}/services/facebook-events`,
};


/* ══════════════════════════════════════════════════════════════
   RESULTS PAGE
   ══════════════════════════════════════════════════════════════ */

export const eventResultsSchema = {
  '@context':   'https://schema.org',
  '@type':      'WebPage',
  '@id':        `${BASE_URL}/event-results#webpage`,
  name:         'Proven Dealership Event Results — Driven2WIN',
  description:  'Real documented results from 6,500+ events nationwide — appointments booked, units sold, and gross profit by location and event type.',
  url:          `${BASE_URL}/event-results`,
  publisher:    { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  speakable: {
    '@type':      'SpeakableSpecification',
    cssSelector:  ['.hero-headline', '.results-headline'],
  },
};


/* ══════════════════════════════════════════════════════════════
   TESTIMONIALS PAGE
   ══════════════════════════════════════════════════════════════ */

export const testimonialsPageSchema = {
  '@context':   'https://schema.org',
  '@type':      'WebPage',
  '@id':        `${BASE_URL}/testimonials#webpage`,
  name:         'Dealer Testimonials — 250+ Success Stories — Driven2WIN',
  url:          `${BASE_URL}/testimonials`,
  description:  '250+ dealer testimonials from automotive, golf cart, motorcycle, and RV dealerships across America sharing real event results.',
  publisher:    { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  aggregateRating: AGGREGATE_RATING,
  review: [
    {
      '@type':       'Review',
      reviewRating:  { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      author:        { '@type': 'Person', name: 'Mike Patterson' },
      reviewBody:    'Driven2WIN brought us 127 appointments in 5 days and we sold 67 units. THE BEST event we\'ve ever run. The team is professional, the results are REAL.',
      itemReviewed:  { '@type': 'Organization', name: 'Driven2WIN', url: BASE_URL },
      publisher:     { '@type': 'Organization', name: 'Tampa Ford' },
    },
    {
      '@type':       'Review',
      reviewRating:  { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      author:        { '@type': 'Person', name: 'Sarah Chen' },
      reviewBody:    'Driven2WIN is hands-down the best event company I\'ve worked with in my 20 years in this business. They handle EVERYTHING and GUARANTEE their numbers.',
      itemReviewed:  { '@type': 'Organization', name: 'Driven2WIN', url: BASE_URL },
      publisher:     { '@type': 'Organization', name: 'Chen Automotive Group' },
    },
    {
      '@type':       'Review',
      reviewRating:  { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      author:        { '@type': 'Person', name: 'David Martinez' },
      reviewBody:    'We were skeptical at first — we\'d been burned by other event companies before. But Driven2WIN delivered EXACTLY what they promised. Professionally executed from start to finish.',
      itemReviewed:  { '@type': 'Organization', name: 'Driven2WIN', url: BASE_URL },
      publisher:     { '@type': 'Organization', name: 'Carolina Auto Sales' },
    },
    {
      '@type':       'Review',
      reviewRating:  { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      author:        { '@type': 'Person', name: 'Tom Bradley' },
      reviewBody:    'We specialize in golf carts and thought offsite events wouldn\'t work for us. We were WRONG! Driven2WIN helped us reach retirement communities and we saw the best ROI we\'ve ever seen.',
      itemReviewed:  { '@type': 'Organization', name: 'Driven2WIN', url: BASE_URL },
      publisher:     { '@type': 'Organization', name: 'Florida Golf Carts' },
    },
    {
      '@type':       'Review',
      reviewRating:  { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      author:        { '@type': 'Person', name: 'Lisa Anderson' },
      reviewBody:    'Their AI appointment setting system is INCREDIBLE. Customers could book 24/7, our calendar was completely PACKED before the event even started. This technology gives a huge competitive advantage.',
      itemReviewed:  { '@type': 'Organization', name: 'Driven2WIN', url: BASE_URL },
      publisher:     { '@type': 'Organization', name: 'Houston Car Center' },
    },
  ],
};


/* ══════════════════════════════════════════════════════════════
   FAQ PAGE — All 18 actual questions
   ══════════════════════════════════════════════════════════════ */

export const faqPageSchema = {
  '@context':   'https://schema.org',
  '@type':      'FAQPage',
  '@id':        `${BASE_URL}/faq#webpage`,
  name:         'Dealership Event Marketing FAQ — Driven2WIN',
  url:          `${BASE_URL}/faq`,
  mainEntity: [
    {
      '@type': 'Question',
      name:    'What types of dealerships do you work with?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'We specialize in automotive, golf cart, ATV, motorcycle, and RV events nationwide. Whether you sell cars, bikes, golf carts, or RVs, we have 23+ years of experience producing high-impact sales events for these industries across all 50 states.',
      },
    },
    {
      '@type': 'Question',
      name:    'Do you work with car dealers, golf cart dealers, motorsports dealers, and RV dealers?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Yes! We work with all of these industries nationwide. Whether you sell cars, bikes, golf carts, or RVs — we have produced thousands of successful events across all these verticals. Our campaigns are tailored to your specific inventory and target market.',
      },
    },
    {
      '@type': 'Question',
      name:    'What geographic areas do you serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'We serve dealerships in all 50 states. Our headquarters is in Jacksonville, FL, but we are a nationwide company with 6,500+ events produced coast to coast. If you are in the United States, we can help you.',
      },
    },
    {
      '@type': 'Question',
      name:    'How much do your services cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Event costs vary based on scope, duration, market size, and services included — offsite setup, direct mail volume, social media budget, and more. We offer Silver, Gold, and Platinum packages to fit different needs and budgets. Contact us for a free custom quote based on your specific goals and market.',
      },
    },
    {
      '@type': 'Question',
      name:    "What's included in your event packages?",
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Our packages include everything needed for a successful event: venue selection and coordination, all permits and compliance handling, professional tent setup for offsite events, direct mail campaigns, Facebook and Instagram promotion, on-site support, and post-event analysis. We handle everything so you can focus on selling.',
      },
    },
    {
      '@type': 'Question',
      name:    'Do you guarantee results?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Yes. We guarantee results. Average show rates are 60%+ on appointments made. If we do not deliver the numbers we promise, we keep working with you until you are satisfied. We stand behind our work 100%.',
      },
    },
    {
      '@type': 'Question',
      name:    'How far in advance should I book an event?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'We recommend booking 6–8 weeks in advance to allow time for permit processing, venue coordination, and marketing campaigns. We can often accommodate rush events with 3–4 weeks notice depending on market availability and permit timelines.',
      },
    },
    {
      '@type': 'Question',
      name:    "What's your typical event timeline?",
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Most events run 3–7 days. The full process from booking to event completion is typically 6–10 weeks: Weeks 1–2 for planning and permits, Weeks 3–5 for marketing campaign launch, Weeks 6–8 for event execution, and Weeks 9–10 for post-event analysis and follow-up.',
      },
    },
    {
      '@type': 'Question',
      name:    'Do I need to provide anything for the event?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'All you need to provide is your inventory and sales team. We handle permits, tents, signage, promotion, appointment setting, and everything else. You focus on closing deals — we take care of the rest.',
      },
    },
    {
      '@type': 'Question',
      name:    'How many appointments can I expect?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Results vary by market, event duration, and package level, but our average event generates 125+ appointments. Our Gold package (5-day events) typically produces 100–150 appointments, while Platinum packages (7-day events) often generate 150–200+ appointments.',
      },
    },
    {
      '@type': 'Question',
      name:    "What's your average conversion rate?",
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Industry average for event-generated appointments is a 40–50% show rate. Our events consistently achieve 60%+ show rates due to our multi-touch marketing approach combining direct mail, social media, SMS, and email. Of those who show up, 70–80% are qualified buyers ready to make a deal.',
      },
    },
    {
      '@type': 'Question',
      name:    'How does your AI appointment setting work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Our AI call center answers incoming calls 24/7, qualifies leads, and books appointments directly into your calendar. It handles overflow during peak times, captures after-hours inquiries, and ensures zero missed opportunities. The AI sounds natural, asks qualifying questions, and schedules appointments around the clock.',
      },
    },
    {
      '@type': 'Question',
      name:    'Can you handle multiple events for the same dealership?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Absolutely. Many clients run quarterly or bi-annual events with us. We help you plan an annual event calendar that maximizes impact while avoiding market fatigue. Regular events build anticipation and train your market to expect your sales events.',
      },
    },
    {
      '@type': 'Question',
      name:    'What makes Driven2WIN different from other event companies?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Three things: First, guaranteed results — we do not just promise numbers, we guarantee them. Second, completely turnkey — we handle everything from permits to promotion to setup. Third, proven track record — 6,500+ events, 23+ years, $250K+ average gross per event.',
      },
    },
    {
      '@type': 'Question',
      name:    'Do you require exclusive contracts?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'No. We work on a per-event basis. We earn your repeat business through performance, not contracts.',
      },
    },
    {
      '@type': 'Question',
      name:    'Can I see examples of your direct mail pieces?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'Yes. Contact us and we will send you samples of our high-performing mailer designs along with response rate data. We are proud of our creative work and happy to show you exactly what we will be sending to your market.',
      },
    },
    {
      '@type': 'Question',
      name:    'How do you handle bad weather during outdoor events?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'All our tents are weather-resistant and professionally installed. For severe weather, we have contingency plans including optional event rescheduling insurance, alternative indoor backup locations, and flexible scheduling to extend events if weather causes disruption.',
      },
    },
    {
      '@type': 'Question',
      name:    "What if I've had bad experiences with event companies before?",
      acceptedAnswer: {
        '@type': 'Answer',
        text:    'We hear this often. Many dealerships have been burned by companies that overpromise and underdeliver. That is why we guarantee our results, provide transparent reporting, maintain open communication throughout the process, and deliver exactly what we promise. Our testimonials from 250+ dealers speak for themselves.',
      },
    },
  ],
};


/* ══════════════════════════════════════════════════════════════
   ABOUT PAGE
   ══════════════════════════════════════════════════════════════ */

export const aboutPageSchema = {
  '@context':   'https://schema.org',
  '@type':      'AboutPage',
  '@id':        `${BASE_URL}/about#webpage`,
  name:         'About Driven2WIN — 23+ Years of Automotive Event Marketing',
  url:          `${BASE_URL}/about`,
  description:  'Founded in 2003, Driven2WIN has produced 6,500+ events in all 50 states. Learn about our history, mission, values, and our commitment to guaranteed dealership results.',
  publisher:    { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  mainEntity:   { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  speakable: {
    '@type':     'SpeakableSpecification',
    cssSelector: ['.about-hero-h1', '.about-mission-text'],
  },
};


/* ══════════════════════════════════════════════════════════════
   CONTACT PAGE
   ══════════════════════════════════════════════════════════════ */

export const contactPageSchema = {
  '@context':   'https://schema.org',
  '@type':      'ContactPage',
  '@id':        `${BASE_URL}/contact#webpage`,
  name:         'Contact Driven2WIN — Free Event Strategy Call',
  url:          `${BASE_URL}/contact`,
  description:  'Schedule a FREE event strategy call with Driven2WIN. Call 904-638-7200 or fill out our form. We respond within 24 hours. Serving all 50 states.',
  publisher:    { '@type': 'Organization', '@id': `${BASE_URL}/#organization` },
  mainEntity: {
    '@type':    'Organization',
    '@id':      `${BASE_URL}/#organization`,
    telephone:  PHONE,
    email:      EMAIL,
    address:    ADDRESS,
  },
};
